// Export presets — destination-specific CSV format recipes.
// Each preset captures the quirks of one platform's importer so the user
// doesn't have to remember "Asana wants no BOM, LF endings, double-quoted".
//
// The user picks a destination from the Export menu; we run Papa.unparse with
// the right options, prepend a BOM if needed, and serve a download with a
// hint in the filename like "myfile-for-asana.csv".
//
// Adding a new preset = adding one row to the PRESETS array. Each preset gets
// its own SEO landing page automatically via the slug.

import Papa from "papaparse";

// One preset = one destination tool with all its quirks bundled.
// Fields:
//   id         — slug; also drives /save-csv-for-{id} landing pages
//   label      — what shows in the Export dropdown
//   tagline    — short description for the dropdown + landing page
//   bom        — prepend UTF-8 BOM (Excel-Windows / Notion need this)
//   delimiter  — comma / tab / semicolon / pipe
//   newline    — line ending: "\n" (LF) or "\r\n" (CRLF)
//   quotes     — papaparse quotes option: true (smart) / false / "all"
//   ext        — file extension (.csv vs .tsv)
//   filenameSuffix — appended to base name e.g. "-for-asana"
//   notes      — surfaced on the landing page to explain WHY this preset exists

export const PRESETS = [
    {
        id: "plain",
        label: "Plain CSV (UTF-8)",
        tagline: "Standard CSV. Works almost everywhere.",
        bom: false,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "",
        notes: "UTF-8 encoded, comma-delimited, LF line endings. The universal CSV format — what most modern tools expect.",
    },
    {
        id: "excel-windows",
        label: "Excel for Windows",
        tagline: "UTF-8 with BOM so Excel shows accents correctly.",
        bom: true,
        delimiter: ",",
        newline: "\r\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-excel",
        notes: "Excel on Windows needs a UTF-8 BOM marker — without it, accented characters (é, ñ, ç) and emoji turn into mojibake (Ã©, Ã±). Also uses CRLF line endings, which Windows tools prefer.",
    },
    {
        id: "excel-mac",
        label: "Excel for Mac / Numbers",
        tagline: "UTF-8 without BOM — Mac Excel hates the BOM.",
        bom: false,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-mac",
        notes: "Excel on Mac and Apple Numbers do the opposite of Windows Excel — a UTF-8 BOM shows up as 'ï»¿' literally in the first cell. So we strip it.",
    },
    {
        id: "asana",
        label: "Save for Asana import",
        tagline: "Strip BOM, LF line endings — what Asana's importer expects.",
        bom: false,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-asana",
        notes: "Asana's CSV importer fails on files saved as 'CSV UTF-8' in Excel because of the byte-order mark. This preset writes plain UTF-8 (no BOM) with LF endings. Required Asana columns: Name, Notes, Assignee, Due Date, Start Date, Section/Column, Tags.",
    },
    {
        id: "trello",
        label: "Save for Trello import",
        tagline: "Plain CSV, US date format.",
        bom: false,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-trello",
        notes: "Trello expects plain UTF-8 with LF endings and MM/DD/YYYY date format. Each row becomes a card. Trello reads card name from the first column.",
    },
    {
        id: "notion",
        label: "Save for Notion import",
        tagline: "UTF-8 with BOM, header row required.",
        bom: true,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-notion",
        notes: "Notion's database CSV importer expects UTF-8 with BOM. The first column becomes each page's title. Other columns become database properties. Dates should be in ISO format (YYYY-MM-DD).",
    },
    {
        id: "salesforce",
        label: "Save for Salesforce",
        tagline: "CRLF endings, every field quoted — Data Loader friendly.",
        bom: false,
        delimiter: ",",
        newline: "\r\n",
        quotes: "all",
        ext: "csv",
        filenameSuffix: "-for-salesforce",
        notes: "Salesforce's Data Loader is strict: CRLF line endings, every text field quoted (even integers — Salesforce doesn't care). Salesforce caps file imports at 50,000 rows per file. If your file is larger, split it before importing.",
    },
    {
        id: "mailchimp",
        label: "Save for Mailchimp audience",
        tagline: "Plain CSV. Match column headers to Mailchimp fields.",
        bom: false,
        delimiter: ",",
        newline: "\n",
        quotes: true,
        ext: "csv",
        filenameSuffix: "-for-mailchimp",
        notes: "Mailchimp expects 'Email Address' as the primary column header (not 'email' or 'Email'). Tags should be in a single column, comma-separated within the cell. Subscribers added via CSV are NOT automatically opted in — they go to a Mailchimp confirmation flow.",
    },
    {
        id: "tsv",
        label: "TSV (tab-separated)",
        tagline: "Tabs instead of commas — clean, simple.",
        bom: false,
        delimiter: "\t",
        newline: "\n",
        quotes: false,
        ext: "tsv",
        filenameSuffix: "",
        notes: "Tab-separated values. Cleaner than CSV for data containing commas or quotes (no escaping needed). Pasted directly into Google Sheets or Excel without any 'import dialog' nonsense.",
    },
];

export function getPreset(id) {
    return PRESETS.find((p) => p.id === id) || PRESETS[0];
}

// Build the export string + filename + MIME for any preset.
// Pass `customOverrides` to use Custom-format dialog inputs that override one
// or two fields of a preset (e.g. "Plain CSV but with semicolons").
export function buildExport(rows2d, preset, baseFilename, customOverrides = {}) {
    const cfg = { ...preset, ...customOverrides };
    const csvText = Papa.unparse(rows2d, {
        delimiter: cfg.delimiter,
        newline: cfg.newline,
        quotes: cfg.quotes,
    });
    // Prepend BOM if the preset asks for it. The BOM is the 3-byte sequence
    // 0xEF 0xBB 0xBF — when written as a UTF-8 string it's "\uFEFF".
    const body = cfg.bom ? "\uFEFF" + csvText : csvText;
    const base = baseFilename.replace(/\.(csv|tsv|txt)$/i, "");
    const filename = `${base}${cfg.filenameSuffix || ""}.${cfg.ext}`;
    const mime = cfg.ext === "tsv" ? "text/tab-separated-values" : "text/csv";
    return {
        body,
        filename,
        mime: `${mime};charset=utf-8;`,
    };
}

// Trigger a browser download from an in-memory string.
export function downloadString(filename, body, mime) {
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
